/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
  lazy,
  Suspense,
  type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTour, useMediaQuery } from '@gokrew/ui/hooks';
import { ConfirmationDialog } from '@gokrew/ui/components';
import {
  buildAssetTypesTourVariant,
  buildAssetTypeSettingsTourVariant,
} from '@/hooks/tours/tourVariants';
import { TOUR_MOCK_ID } from '@/hooks/tours/tourMocks';
import { TranslationNS } from '@/constants/translation-namespaces';
import { useTourContext, useDialog } from '@gokrew/ui';
import {
  useAssetTypes,
  useAssetTypeById,
} from '@/hooks/asset-types/useAssetTypes';
import {
  useFieldSections,
  useFieldSets,
} from '@/hooks/asset-types/useAssetTypeFields';
import { getConfig } from '@gokrew/ui/runtime-config';
import { useAuth } from '@/context/AuthContext';
import { OnboardingFeatureKey } from '@gokrew/models/company';

const TourGuide = lazy(() =>
  import('@gokrew/ui/components/TourGuide/TourGuide').then((m) => ({
    default: m.TourGuide,
  }))
);

/** Which Asset Types tab the active tour step spotlights, if any. */
type TourSection = 'system' | 'company' | null;

interface AssetTourContextValue {
  /** True while the "Understand Asset Types" tour is running. */
  isTourActive: boolean;
  /** True while the full Assets tour spotlights the first assets table row. */
  isAssetsTableStep: boolean;
  /**
   * The tab whose table the active step spotlights. The page selects this tab
   * and injects a mock row into its table when empty. `null` off the table steps.
   */
  tourSection: TourSection;
  /** The asset type the tour opens — a real company type, or the mock sentinel. */
  demoTypeId: string;
  /**
   * Lets the create-asset form (mounted during the tour's last steps) report
   * whether it has unsaved data, so finishing/skipping the tour there can
   * confirm before navigating away and discarding it.
   */
  setCreateFormDirty: (dirty: boolean) => void;
  /**
   * True once the user has actually opened the active step's tooltip (not
   * merely armed its beacon on auto-start). UI that locks/disables itself in
   * response to `tourSection` should also require this, so it doesn't lock
   * before the user has engaged with the tour at all (KH_17).
   */
  isTourStepEngaged: boolean;
}

const AssetTourContext = createContext<AssetTourContextValue>({
  isTourActive: false,
  isAssetsTableStep: false,
  tourSection: null,
  demoTypeId: TOUR_MOCK_ID,
  setCreateFormDirty: () => {},
  isTourStepEngaged: false,
});

export function useAssetTour() {
  return useContext(AssetTourContext);
}

/**
 * Warms the queries the settings/create-form steps depend on so those pages
 * render their `data-tour` targets on first paint (no TARGET_NOT_FOUND race).
 * Mounted only while the tour runs; renders nothing.
 */
function AssetTourWarmup({ demoTypeId }: { demoTypeId: string }) {
  useFieldSections();
  useFieldSets();
  // No-ops for the mock sentinel (gated by isValidUUID inside the hook).
  useAssetTypeById(demoTypeId);
  return null;
}

/**
 * Hosts the Asset Types tour above page level so its run/step state survives
 * navigation between `/assets` and `/asset-types`. The header trigger is
 * page-specific: `/assets` starts the full flow, while `/asset-types` starts
 * the focused basics/settings flow. The mock/demo state it exposes is consumed
 * by the asset pages through {@link useAssetTour}.
 */
export function AssetTourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation(TranslationNS.Tour);
  const { registerTour } = useTourContext();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { featureFlags } = useAuth();
  const { openDialog } = useDialog();

  const isCreateFormDirtyRef = useRef(false);
  const setCreateFormDirty = useCallback((dirty: boolean) => {
    isCreateFormDirtyRef.current = dirty;
  }, []);

  const confirmDiscardIfDirty = useCallback(
    (onProceed: () => void) => {
      if (!isCreateFormDirtyRef.current) {
        onProceed();
        return;
      }
      openDialog({
        title: t('asset_types.discard_dialog.title'),
        content: (
          <ConfirmationDialog
            variant="warning"
            onSubmit={onProceed}
            bullets={[t('asset_types.discard_dialog.message')]}
          />
        ),
      });
    },
    [openDialog, t]
  );

  // The tour spans /assets and /asset-types, with one trigger per route. Both
  // pages show their get-started panel until the Assets feature is enabled, so
  // there is nothing to spotlight — keep the tour off entirely until then.
  const isAssetsEnabled = !!featureFlags?.[OnboardingFeatureKey.Assets];
  const isAssetRoute =
    isAssetsEnabled && (pathname === '/assets' || pathname === '/asset-types');

  // Resolve the demo type on either asset route so it's ready whichever page the
  // tour is started from — otherwise this query would fire on every
  // authenticated route. React Query keeps the cached result across navigation.
  const { data: companyTypes = [] } = useAssetTypes(
    { page: 1, limit: 99, type: 'company' },
    { enabled: getConfig().ENABLE_TOUR && isAssetRoute }
  );
  const demoTypeId = companyTypes[0]?.id ?? TOUR_MOCK_ID;

  // Which variant runs is locked in at trigger time (each header-menu entry
  // pins its own flow) so mid-tour navigation doesn't swap it. A direct visit
  // to /asset-types (bookmark, fresh tab) must auto-start the settings-only
  // flow — its first step targets this page, unlike the full flow's, which
  // targets /assets and would auto-start mid-sequence via TARGET_NOT_FOUND.
  const [isSettingsFlow, setIsSettingsFlow] = useState(
    () => pathname === '/asset-types'
  );

  // Every page a running tour's own `onEnter` can navigate to, so the run
  // survives leaving `isAssetRoute`'s two entry pages — computed ahead of the
  // `useTour` call below (rather than only feeding `stopTour`, as before) so
  // both the tour's own autostart-vs-active gating and whether `TourGuide`
  // stays mounted agree with each other; without this, navigating to the
  // settings page mid-tour tore `TourGuide` down entirely (tooltip vanishes,
  // no way to advance) and separately could re-trigger the autostart effect.
  const settingsPagePath = `/asset-types/${demoTypeId}/settings`;
  const isWithinRunningTourScope = isSettingsFlow
    ? pathname === '/asset-types' || pathname === settingsPagePath
    : pathname === '/assets' ||
      pathname === '/asset-types' ||
      pathname === settingsPagePath ||
      pathname === '/assets/create';

  const fullVariant = useMemo(
    () =>
      buildAssetTypesTourVariant(navigate, demoTypeId, confirmDiscardIfDirty),
    [navigate, demoTypeId, confirmDiscardIfDirty]
  );
  const settingsVariant = useMemo(
    () => buildAssetTypeSettingsTourVariant(navigate, demoTypeId),
    [navigate, demoTypeId]
  );
  const variant = isSettingsFlow ? settingsVariant : fullVariant;

  // The full flow's own scope includes the settings/create-form pages a
  // running tour navigates itself to — passing that (not just the two entry
  // pages) keeps `useTour`'s autostart-vs-active effect from treating that
  // navigation as leaving the tour's territory mid-run.
  const isTourRouteActive = isAssetRoute || isWithinRunningTourScope;

  // Auto-start arms the first step's beacon (and resolves its target/section)
  // immediately, before the user has clicked anything — locking a tab purely
  // because of that would make the lock appear arbitrarily on first page
  // load (KH_17). Only start locking tabs once the user has actually opened
  // a step's tooltip.
  const [hasEngagedStep, setHasEngagedStep] = useState(false);
  const onTooltipOpen = useCallback(() => setHasEngagedStep(true), []);

  const {
    steps,
    run,
    skipBeacon,
    onFinish: finishTour,
    activeTarget,
    onStepChange,
    startTour,
    stopTour,
  } = useTour(variant, {
    enabled: isTourRouteActive,
    tableTargetKey: 'asset-types-table',
    autoStart: true,
    // Each route registers its own flow below so the trigger starts the
    // matching variant; skip the default entry to avoid a duplicate.
    registerTrigger: false,
  });

  // TourGuide is mounted at this provider's level, so a running tour keeps
  // trying to resolve its current step's target even after the user
  // navigates somewhere the running flow never sends it (e.g. clicking any
  // other sidebar link, or opening an asset's edit page). With the target
  // gone, Joyride treats it as TARGET_NOT_FOUND and silently auto-advances
  // to the next step — running that step's onEnter (a navigate() call) on
  // top of whatever the user actually clicked. Stop the run the moment we
  // leave the running variant's own route footprint so it can't hijack
  // unrelated navigation — this must cover every page a step's onEnter can
  // send the user to, not just the two entry routes `isAssetRoute` checks,
  // or the fix would abort the tour's own legitimate mid-flow navigation.
  useEffect(() => {
    if (!isWithinRunningTourScope && run) stopTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWithinRunningTourScope]);

  // Reset engagement tracking whenever the run ends, so the next auto-start
  // (or manual restart) begins un-engaged again.
  useEffect(() => {
    if (!run) setHasEngagedStep(false);
  }, [run]);

  // Each route pins its own variant before starting, so `/assets` and
  // `/asset-types` remain separate entry points instead of showing a menu.
  const startFullTour = useCallback(() => {
    setIsSettingsFlow(false);
    startTour();
  }, [startTour]);

  const startSettingsTour = useCallback(() => {
    setIsSettingsFlow(true);
    startTour();
  }, [startTour]);

  useEffect(() => {
    if (!getConfig().ENABLE_TOUR || !isDesktop || !isAssetRoute) return;
    if (pathname === '/asset-types') {
      return registerTour({
        id: 'asset-type-settings',
        label: t('guide.take_tour'),
        start: startSettingsTour,
      });
    }
    return registerTour({
      id: 'asset-types',
      label: t('guide.take_tour'),
      start: startFullTour,
    });
  }, [
    registerTour,
    t,
    isDesktop,
    isAssetRoute,
    pathname,
    startSettingsTour,
    startFullTour,
  ]);

  // Reset to the full flow once a tour ends so a lingering settings flow can't
  // affect a later /assets visit.
  const onFinish = useCallback(() => {
    finishTour();
    setIsSettingsFlow(false);
  }, [finishTour]);

  // Map the spotlighted target to its tab. Check the more specific system key
  // first — "asset-types-system-table" does not contain "asset-types-table".
  const tourSection = useMemo<TourSection>(() => {
    if (!activeTarget) return null;
    if (activeTarget.includes('asset-types-system-table')) return 'system';
    if (activeTarget.includes('asset-types-table')) return 'company';
    return null;
  }, [activeTarget]);
  const isAssetsTableStep = activeTarget?.includes('assets-table') ?? false;

  const value = useMemo<AssetTourContextValue>(
    () => ({
      isTourActive: run,
      isAssetsTableStep,
      tourSection,
      demoTypeId,
      setCreateFormDirty,
      isTourStepEngaged: hasEngagedStep,
    }),
    [
      run,
      isAssetsTableStep,
      tourSection,
      demoTypeId,
      setCreateFormDirty,
      hasEngagedStep,
    ]
  );

  return (
    <AssetTourContext.Provider value={value}>
      {children}
      {run && <AssetTourWarmup demoTypeId={demoTypeId} />}
      {isTourRouteActive && (
        <Suspense fallback={null}>
          <TourGuide
            steps={steps}
            run={run}
            skipBeacon={skipBeacon}
            onFinish={onFinish}
            onStepChange={onStepChange}
            onTooltipOpen={onTooltipOpen}
          />
        </Suspense>
      )}
    </AssetTourContext.Provider>
  );
}
